import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Search, UserCheck, X } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';

const formSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter no minimo 2 caracteres'),
  descricao: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface SelectedMember {
  userId: string;
  nome: string;
  email: string;
  funcao: string;
}

interface ComissaoFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventoId?: string;
  campanhaId?: string;
  comissaoId?: string;
}

export function ComissaoFormModal({
  isOpen,
  onClose,
  eventoId,
  campanhaId,
  comissaoId,
}: ComissaoFormModalProps) {
  const utils = trpc.useUtils();
  const isEditing = !!comissaoId;

  const [userSearch, setUserSearch] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<SelectedMember[]>([]);

  const { data: comissao } = trpc.comissoes.getById.useQuery(
    { id: comissaoId! },
    { enabled: !!comissaoId && isOpen }
  );

  // Fetch existing members when editing
  const { data: existingMembrosData } = trpc.comissoes.listMembros.useQuery(
    { comissaoId: comissaoId!, page: 1, limit: 100 },
    { enabled: !!comissaoId && isOpen }
  );
  const existingMembros = existingMembrosData?.items;

  const { data: allUsers } = trpc.comissoes.searchUsers.useQuery(
    { search: userSearch || undefined },
    { enabled: isOpen }
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (isOpen) {
      if (comissao && isEditing) {
        reset({ nome: comissao.nome, descricao: comissao.descricao || '' });
      } else {
        reset({ nome: '', descricao: '' });
        setSelectedMembers([]);
      }
      setUserSearch('');
    }
  }, [comissao, isEditing, isOpen, reset]);

  // When editing, pre-populate selected members from existing membros
  useEffect(() => {
    if (existingMembros && isEditing) {
      setSelectedMembers(
        existingMembros
          .filter((m) => m.userId)
          .map((m) => ({
            userId: m.userId!,
            nome: m.nome,
            email: m.email || '',
            funcao: m.funcao,
          }))
      );
    }
  }, [existingMembros, isEditing]);

  const createMutation = trpc.comissoes.create.useMutation({
    onSuccess: async (newComissao) => {
      // Add selected members
      if (selectedMembers.length > 0 && newComissao) {
        for (const member of selectedMembers) {
          await addMembroMutation.mutateAsync({
            comissaoId: newComissao.id,
            userId: member.userId,
            nome: member.nome,
            funcao: member.funcao,
            email: member.email,
          });
        }
      }
      utils.comissoes.list.invalidate();
      if (eventoId) utils.eventos.getById.invalidate({ id: eventoId });
      handleClose();
    },
    onError: (error: { message: string }) => alert('Erro ao criar: ' + error.message),
  });

  const updateMutation = trpc.comissoes.update.useMutation({
    onSuccess: async () => {
      // Sync members: add new ones, remove unselected ones
      if (comissaoId && existingMembros) {
        const existingUserIds = new Set(existingMembros.filter((m) => m.userId).map((m) => m.userId!));
        const selectedUserIds = new Set(selectedMembers.map((m) => m.userId));

        // Add new members
        for (const member of selectedMembers) {
          if (!existingUserIds.has(member.userId)) {
            await addMembroMutation.mutateAsync({
              comissaoId: comissaoId!,
              userId: member.userId,
              nome: member.nome,
              funcao: member.funcao,
              email: member.email,
            });
          }
        }

        // Remove deselected members (only user-linked ones)
        for (const membro of existingMembros) {
          if (membro.userId && !selectedUserIds.has(membro.userId)) {
            await removeMembroMutation.mutateAsync({ id: membro.id });
          }
        }
      }
      utils.comissoes.list.invalidate();
      if (comissaoId) {
        utils.comissoes.getById.invalidate({ id: comissaoId });
        utils.comissoes.listMembros.invalidate({ comissaoId });
      }
      handleClose();
    },
    onError: (error: { message: string }) => alert('Erro ao atualizar: ' + error.message),
  });

  const addMembroMutation = trpc.comissoes.addMembro.useMutation();
  const removeMembroMutation = trpc.comissoes.removeMembro.useMutation();

  const handleClose = () => {
    reset({ nome: '', descricao: '' });
    setSelectedMembers([]);
    setUserSearch('');
    onClose();
  };

  const onSubmit = (data: FormData) => {
    if (isEditing) {
      updateMutation.mutate({ id: comissaoId!, ...data });
    } else {
      createMutation.mutate({ ...data, eventoId, campanhaId });
    }
  };

  const toggleUser = (user: { id: string; nome: string; email: string }) => {
    setSelectedMembers((prev) => {
      const exists = prev.find((m) => m.userId === user.id);
      if (exists) {
        return prev.filter((m) => m.userId !== user.id);
      }
      return [...prev, { userId: user.id, nome: user.nome, email: user.email, funcao: 'Membro' }];
    });
  };

  const updateMemberFuncao = (userId: string, funcao: string) => {
    setSelectedMembers((prev) =>
      prev.map((m) => (m.userId === userId ? { ...m, funcao } : m))
    );
  };

  const isUserSelected = (userId: string) => selectedMembers.some((m) => m.userId === userId);

  // Filter users based on search
  const filteredUsers = allUsers || [];

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Editar Comissao' : 'Nova Comissao'}
      size="xl"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Nome"
          placeholder="Ex: Logistica, Decoracao, etc."
          {...register('nome')}
          error={errors.nome?.message}
        />

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Descricao</label>
          <textarea
            {...register('descricao')}
            rows={2}
            placeholder="Descricao opcional..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        {/* Members section */}
        <div className="border-t border-gray-200 pt-4">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Vincular Membros ({selectedMembers.length} selecionado{selectedMembers.length !== 1 ? 's' : ''})
          </label>

          {/* Selected members chips */}
          {selectedMembers.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {selectedMembers.map((member) => (
                <div
                  key={member.userId}
                  className="flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 pl-3 pr-1.5 py-1"
                >
                  <UserCheck className="h-3.5 w-3.5 text-primary-600" />
                  <span className="text-sm text-primary-800">{member.nome}</span>
                  <span className="text-xs text-primary-500">({member.funcao})</span>
                  <button
                    type="button"
                    onClick={() => toggleUser({ id: member.userId, nome: member.nome, email: member.email })}
                    className="ml-0.5 rounded-full p-0.5 text-primary-400 hover:bg-primary-100 hover:text-primary-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Funcao editor for selected members */}
          {selectedMembers.length > 0 && (
            <div className="mb-3 space-y-2 rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Funcoes</p>
              {selectedMembers.map((member) => (
                <div key={member.userId} className="flex items-center gap-2">
                  <span className="text-sm text-gray-700 w-32 truncate">{member.nome}</span>
                  <input
                    type="text"
                    value={member.funcao}
                    onChange={(e) => updateMemberFuncao(member.userId, e.target.value)}
                    placeholder="Ex: Coordenador"
                    className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Search */}
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Buscar usuario..."
              className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {/* Users list */}
          <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200">
            {filteredUsers.length === 0 ? (
              <div className="py-4 text-center text-sm text-gray-500">
                Nenhum usuario encontrado
              </div>
            ) : (
              filteredUsers.map((user) => {
                const selected = isUserSelected(user.id);
                return (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => toggleUser(user)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-gray-100 last:border-0 transition-colors ${
                      selected
                        ? 'bg-primary-50'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`flex h-5 w-5 items-center justify-center rounded border ${
                      selected
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-gray-300'
                    }`}>
                      {selected && (
                        <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{user.nome}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button type="submit" isLoading={isPending}>
            {isEditing ? 'Salvar' : 'Criar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
